var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// (c) 2023 Acellera Ltd http://www.acellera.com
// All Rights Reserved
// No redistribution in whole or part
//
import { TrajectoryModeEnum, } from "../..";
import { PluginStateObject } from "molstar/lib/mol-plugin-state/objects";
import { StateObjectRef } from "molstar/lib/mol-state";
import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Grid, FormHelperText, Tooltip, } from "@mui/material";
import { ControlledSlider } from "../Controls/Slider/ControlledSlider";
import { throttle_updateFrame } from "../showTraj";
import { updateStateTreeTrajectory } from "../stateTree";
import { PlayPauseButton } from "./PlayPauseButton";
import { CUSTOM_AnimateModelIndex } from "./TrajectoryAnimation";
import { shallow } from "zustand/shallow";
import { TrajectoryModeSelect } from "./TrajectoryModeSelect";
import { ControlledInputNumberFocusOut, ControlledInputRangeFocusOut, } from "../Controls/Input/ControlledInputFocusOut";
import { getCurrentTrajMultipleFramesSettings } from "./utils";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
export function TrajectoryFrameControls({ vss, molstar, trajCellRef, visibility, positionInTree, trajectoryMode, handleDisplayMultipleFrames, handleSelectFrameMode, handleSmoothTraj, defaultFrame, defaultStep, defaultSmooth, wait, }) {
    const trajectoryModeName = (trajectoryMode === null || trajectoryMode === void 0 ? void 0 : trajectoryMode.name)
        ? trajectoryMode.name
        : TrajectoryModeEnum.currentFrame;
    return (_jsx(Box, Object.assign({ sx: { px: 1 } }, { children: _jsx(Box, { children: trajectoryModeName === TrajectoryModeEnum.currentFrame ? (_jsx(TrajectoryFrameControlsPlay, { vss: vss, molstar: molstar, trajCellRef: trajCellRef, visibility: visibility, positionInTree: positionInTree, handleSelectFrameMode: handleSelectFrameMode, defaultFrame: defaultFrame, defaultStep: defaultStep, defaultSmooth: defaultSmooth, trajectoryMode: trajectoryModeName, wait: wait, handleSmoothTraj: handleSmoothTraj })) : (_jsx(TrajectoryFrameControlsMultiple, { vss: vss, molstar: molstar, trajCellRef: trajCellRef, visibility: visibility, positionInTree: positionInTree, handleSelectFrameMode: handleSelectFrameMode, handleDisplayMultipleFrames: handleDisplayMultipleFrames, defaultFrame: defaultFrame, defaultStep: defaultStep, trajectoryMode: trajectoryMode, wait: wait })) }) })));
}
const getTrajectoryDefaultParams = (trajectory, defaultFrame, defaultStep, defaultSmooth) => {
    var _a;
    const frameCount = (_a = trajectory === null || trajectory === void 0 ? void 0 : trajectory.obj) === null || _a === void 0 ? void 0 : _a.data.frameCount;
    if (frameCount === undefined) {
        console.error("Trajectory frames are undefined.");
        return {
            frameCount: 0,
            controlParameters: undefined,
            defaultFrameNum: 1,
            defaultStepNum: 1,
            defaultSmoothVal: 0,
        };
    }
    const controlParameters = {
        minVal: 1,
        maxVal: frameCount,
    };
    const defaultFrameNum = defaultFrame && defaultFrame <= frameCount ? defaultFrame : 1;
    const defaultStepNum = defaultStep && defaultStep <= frameCount ? defaultStep : 1;
    const defaultSmoothVal = defaultSmooth ? defaultSmooth : 0;
    return {
        frameCount,
        controlParameters,
        defaultFrameNum,
        defaultStepNum,
        defaultSmoothVal,
    };
};
function TrajectoryFrameControlsPlay({ vss, molstar, trajCellRef, visibility, positionInTree, trajectoryMode, 
// handleDisplayMultipleFrames,
handleSelectFrameMode, handleSmoothTraj, defaultFrame, defaultStep, defaultSmooth, wait, }) {
    const trajectory = StateObjectRef.resolveAndCheck(molstar.state.data, trajCellRef);
    const [activeTrajectory, setActiveTrajectory] = vss((state) => [state.activeTrajectory, state.setActiveTrajectory], shallow);
    const { frameCount, controlParameters, defaultFrameNum, defaultStepNum, defaultSmoothVal, } = getTrajectoryDefaultParams(trajectory, defaultFrame, defaultStep, defaultSmooth);
    const [currFrame, setFrame] = useState(defaultFrameNum);
    const [stepSize, setStepSize] = useState(defaultStepNum);
    const [smoothVal, setSmoothVal] = useState(defaultSmoothVal);
    const isPlaying = activeTrajectory === trajCellRef;
    const onSetStep = (newStep) => {
        if (activeTrajectory)
            return;
        setStepSize(newStep);
        updateStateTreeTrajectory(vss, positionInTree, "step", newStep);
    };
    const onSetSmooth = (newSmooth) => {
        if (newSmooth === smoothVal)
            return;
        if (activeTrajectory)
            return;
        setSmoothVal(newSmooth);
        updateStateTreeTrajectory(vss, positionInTree, "smooth", newSmooth);
        handleSmoothTraj(newSmooth);
    };
    const onUpdateCallback = (newFrame, skipTreeUpdate) => __awaiter(this, void 0, void 0, function* () {
        if (!molstar)
            return;
        if (activeTrajectory)
            return;
        throttle_updateFrame(molstar, newFrame, trajCellRef);
        setFrame(newFrame);
        if (!skipTreeUpdate) {
            updateStateTreeTrajectory(vss, positionInTree, "frame", newFrame);
        }
    });
    const startAnimation = useCallback(() => {
        molstar.managers.animation.play(CUSTOM_AnimateModelIndex, {
            duration: {
                name: "sequential",
                params: {
                    maxFps: 8,
                    // targetFps: 8
                },
            },
            mode: { name: "loop", params: { direction: "forward" } },
            cellRef: trajCellRef,
            step: stepSize,
        });
    }, [trajCellRef, molstar, stepSize]);
    const stopAnimation = useCallback(() => {
        molstar.managers.animation.stop();
        updateStateTreeTrajectory(vss, positionInTree, "frame", currFrame);
    }, [currFrame, positionInTree, vss, molstar]);
    molstar.state.events.object.updated.subscribe((e) => {
        const state = molstar.state.data;
        if (!state)
            return null;
        const models = state.selectQ((q) => q
            .byRef(trajCellRef)
            .subtree()
            .ofType(PluginStateObject.Molecule.Model)
            .first());
        const idx = models[0].transform
            .params.modelIndex;
        setFrame(idx + 1);
    });
    useEffect(() => {
        if (isPlaying) {
            startAnimation();
        }
        else {
            stopAnimation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying]);
    const _handleSelectFrameMode = (mode) => {
        handleSelectFrameMode(mode, currFrame);
    };
    const trajError = controlParameters === undefined;
    return (_jsx(_Fragment, { children: !trajError && (_jsxs(Grid, Object.assign({ container: true, 
            // columnSpacing={1}
            direction: "row", alignItems: "center", justifyContent: "flex-start", sx: { pt: 1 } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1, pl: "8px" }, alignItems: "center" }, { children: [_jsx(Typography, Object.assign({ sx: { fontSize: "0.75rem" }, component: FormHelperText }, { children: "Show:" })), _jsx(Box, Object.assign({ sx: { width: "4.4em" } }, { children: _jsx(TrajectoryModeSelect, { callback: _handleSelectFrameMode, disabled: !!activeTrajectory || !!wait, currentTrajectoryMode: trajectoryMode }) }))] })) })), _jsx(Grid, Object.assign({ item: true, xs: "auto" }, { children: _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1, pl: "8px" }, alignItems: "center" }, { children: [_jsxs(Typography, Object.assign({ sx: { fontSize: "0.75rem" }, component: FormHelperText }, { children: ["Step:", " "] })), _jsx(Box, Object.assign({ sx: { width: "2.5em" } }, { children: _jsx(ControlledInputNumberFocusOut, Object.assign({ fontSize: "0.75rem" }, controlParameters, { currVal: stepSize, disabled: !visibility || !!activeTrajectory || !!wait, onUpdate: onSetStep, onlyIntegers: true })) }))] })) })), _jsx(Grid, Object.assign({ item: true, xs: "auto" }, { children: _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1, pl: "8px" }, alignItems: "center" }, { children: [_jsxs(Typography, Object.assign({ sx: { fontSize: "0.75rem" }, component: FormHelperText }, { children: ["Smooth:", " "] })), _jsx(Box, Object.assign({ sx: { width: "2em" } }, { children: _jsx(ControlledInputNumberFocusOut, { fontSize: "0.75rem", minVal: 0, maxVal: 1000, currVal: smoothVal, disabled: !visibility || !!activeTrajectory || !!wait, onUpdate: onSetSmooth, onlyIntegers: true }) }))] })) })), _jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1, pl: "-10px", width: "100%" }, alignItems: "center" }, { children: [_jsx(Box, { children: _jsx(PlayPauseButton, { isPlaying: isPlaying, disabled: !visibility || !!wait, onClick: () => setActiveTrajectory(isPlaying ? undefined : trajCellRef) }) }), _jsx(Box, Object.assign({ onClick: () => setActiveTrajectory(undefined), sx: { minWidth: "3.5em", width: "100%" } }, { children: _jsx(ControlledSlider, Object.assign({}, controlParameters, { currVal: currFrame, disabled: !visibility || !!wait, onUpdate: onUpdateCallback, onMove: (newFrame) => onUpdateCallback(newFrame, true), step: stepSize, valueLabelDisplay: "off" })) }))] })) })), _jsxs(Grid, Object.assign({ item: true, container: true, direction: "row", alignItems: "center", xs: "auto", columnSpacing: 1, sx: { mx: 1 } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true, sx: { py: 1 } }, { children: _jsx(Box, Object.assign({ sx: { width: "2.5em" } }, { children: _jsx(ControlledInputNumberFocusOut, Object.assign({}, controlParameters, { currVal: currFrame, disabled: !visibility || !!activeTrajectory || !!wait, onUpdate: onUpdateCallback, onlyIntegers: true, step: stepSize, fontSize: "0.75rem" })) })) })), _jsx(Grid, Object.assign({ item: true, xs: "auto" }, { children: _jsx(Typography, Object.assign({ sx: {
                                    color: visibility ? "black" : "#808080",
                                    fontSize: "0.75rem",
                                } }, { children: `/ ${frameCount}` })) }))] }))] }))) }));
}
function TrajectoryFrameControlsMultiple({ vss, molstar, trajCellRef, visibility, positionInTree, trajectoryMode, handleDisplayMultipleFrames, handleSelectFrameMode, defaultFrame, defaultStep, wait, }) {
    const trajectoryModeName = (trajectoryMode === null || trajectoryMode === void 0 ? void 0 : trajectoryMode.name)
        ? trajectoryMode.name
        : TrajectoryModeEnum.currentFrame;
    const trajectory = StateObjectRef.resolveAndCheck(molstar.state.data, trajCellRef);
    const { frameCount, controlParameters, defaultFrameNum, defaultStepNum } = getTrajectoryDefaultParams(trajectory, defaultFrame, defaultStep);
    const { from, to, step } = getCurrentTrajMultipleFramesSettings(trajectoryMode === null || trajectoryMode === void 0 ? void 0 : trajectoryMode.options);
    const [frameSettings, setFrameSettings] = useState({
        from: from,
        to: to,
        step: step,
    });
    useEffect(() => {
        setFrameSettings({
            from: from,
            to: to,
            step: step,
        });
    }, [from, to, step]);
    const _handleSelectFrameMode = (mode) => {
        handleSelectFrameMode(mode, 1);
    };
    const handleUpdate = (newFrom, newTo, newStep) => {
        setFrameSettings((prev) => {
            return {
                from: newFrom,
                to: newTo,
                step: newStep ? newStep : 1,
            };
        });
    };
    useEffect(() => {
        if (frameSettings.from === from &&
            frameSettings.to === to &&
            frameSettings.step === step)
            return;
        handleDisplayMultipleFrames(frameSettings.from, frameSettings.to, frameSettings.step);
    }, [frameSettings.from, frameSettings.to, frameSettings.step]);
    return (_jsxs(Grid, Object.assign({ container: true, direction: "column", justifyContent: "center", alignItems: "flex-start", sx: { py: 1 } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1, pl: "8px" }, alignItems: "center" }, { children: [_jsx(Typography, Object.assign({ sx: { fontSize: "0.75rem" }, component: FormHelperText }, { children: "Show:" })), _jsx(Box, Object.assign({ sx: { width: "4.4em" } }, { children: _jsx(TrajectoryModeSelect, { disabled: !!wait, callback: _handleSelectFrameMode, currentTrajectoryMode: trajectoryModeName }) }))] })) })), _jsxs(Grid, Object.assign({ container: true, item: true, xs: true, 
                // columnSpacing={1}
                direction: "row", alignItems: "center", justifyContent: "flex-start", spacing: 1 }, { children: [_jsx(Grid, Object.assign({ item: true, xs: "auto" }, { children: _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1, pl: "8px" }, alignItems: "center" }, { children: [_jsx(Typography, Object.assign({ sx: { fontSize: "0.75rem" }, component: FormHelperText }, { children: "Selection:" })), _jsx(Box, Object.assign({ sx: { width: "5em" } }, { children: _jsx(ControlledInputRangeFocusOut, { currValFrom: frameSettings.from, currValTo: frameSettings.to, currValStep: frameSettings.step, minVal: 1, maxVal: frameCount, onUpdate: handleUpdate, onlyIntegers: true, disabled: !!wait, fontSize: "0.75rem" }) }))] })) })), _jsx(Grid, Object.assign({ item: true, xs: "auto" }, { children: _jsx(SelectorInstructions, { disabled: !!wait }) }))] }))] })));
}
const SelectorInstructions = ({ disabled }) => {
    const instructions = `
Enter a range using the following formats: b:e for start and end values (e.g., 1:10) or b:s:e to include a step value (e.g., 1:2:10 for a range from 1 to 10 with steps od 2)`;
    const color = disabled ? "#808080" : "secondary.main";
    return (_jsx(Tooltip, Object.assign({ arrow: true, placement: "top", title: instructions }, { children: _jsx(HelpOutlineIcon, { sx: { color: color, fontSize: "1rem" } }) })));
};
